import React from 'react';
import { X, AlertTriangle, Info } from 'lucide-react';

interface InsufficientDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyAddress?: string;
  stationObjectId?: number;
}

export default function InsufficientDataModal({
  isOpen,
  onClose,
  propertyAddress,
  stationObjectId
}: InsufficientDataModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Projection Data Unavailable
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            Unfortunately, we don't have enough market data for this property to generate accurate AI projections at this time.
          </p>

          {propertyAddress && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Property:</p>
              <p className="text-sm font-medium text-gray-900">{propertyAddress}</p>
              {stationObjectId && (
                <p className="text-xs text-gray-500 mt-1">ID: {stationObjectId}</p>
              )}
            </div>
          )}

          <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  What You Can Do
                </h4>
                <p className="text-sm text-blue-800">
                  You're welcome to continue with the valuation by entering your own financial values in the input fields below. This gives you full control over the numbers used in your valuation.
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Note:</strong> We've logged this property for future data collection. As we gather more market data for this area, projections may become available.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
}
